import * as chrono from 'chrono-node';
import type { TaskPriority } from '@/types/todolist';
import type { RecurrenceRule } from '@/types/recurrence';
import { parseRecurrenceFromText, formatRecurrence } from '@/types/recurrence';

// Token types that can be parsed from input
export type ParseTokenType = 'date' | 'time' | 'priority' | 'recurrence' | 'text';

// Represents a parsed segment of the input
export interface ParseToken {
  type: ParseTokenType;
  value: string;
  start: number;
  end: number;
  displayValue: string; // Human-readable display
}

// Result of parsing a task input string
export interface ParsedTask {
  title: string; // Cleaned title with parsed tokens removed
  dueDate: Date | null;
  hasTime: boolean; // Whether a specific time was parsed
  priority: TaskPriority | null;
  recurrence: RecurrenceRule | null;
  tokens: ParseToken[];
  originalText: string;
}

// Priority patterns to recognize
const PRIORITY_PATTERNS: Array<{
  pattern: RegExp;
  priority: TaskPriority;
  display: string;
}> = [
  { pattern: /\bp1\b/i, priority: 'high', display: 'High' },
  { pattern: /(?:^|\s)!!!(?:\s|$)/, priority: 'high', display: 'High' },
  { pattern: /\bhigh\s+priority\b/i, priority: 'high', display: 'High' },
  { pattern: /\bp2\b/i, priority: 'medium', display: 'Medium' },
  { pattern: /(?:^|\s)!!(?:\s|$)/, priority: 'medium', display: 'Medium' },
  { pattern: /\bmedium\s+priority\b/i, priority: 'medium', display: 'Medium' },
  { pattern: /\bp3\b/i, priority: 'low', display: 'Low' },
  { pattern: /(?:^|\s)!(?:\s|$)/, priority: 'low', display: 'Low' },
  { pattern: /\blow\s+priority\b/i, priority: 'low', display: 'Low' },
];

/**
 * Parse priority from input text
 */
const parsePriority = (
  text: string
): { priority: TaskPriority | null; token: ParseToken | null; cleanedText: string } => {
  for (const { pattern, priority, display } of PRIORITY_PATTERNS) {
    const match = text.match(pattern);
    if (match && match.index !== undefined) {
      const token: ParseToken = {
        type: 'priority',
        value: match[0],
        start: match.index,
        end: match.index + match[0].length,
        displayValue: display,
      };

      // Remove the priority from text
      const cleanedText = (text.slice(0, match.index) + text.slice(match.index + match[0].length))
        .replace(/\s+/g, ' ')
        .trim();

      return { priority, token, cleanedText };
    }
  }

  return { priority: null, token: null, cleanedText: text };
};

// Recurrence patterns that should be extracted from text (in order of specificity)
const RECURRENCE_PATTERNS: RegExp[] = [
  /\bevery\s+weekday\b/i,
  /\bweekdays\b/i,
  /\bevery\s+\d+\s+(day|days|week|weeks|month|months|year|years)\b/i,
  /\bevery\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i,
  /\bevery\s+(day|week|month|year)\b/i,
  /\b(daily|weekly|monthly|yearly|biweekly|annually)\b/i,
];

/**
 * Parse recurrence pattern from input text
 */
const parseRecurrence = (
  text: string
): { recurrence: RecurrenceRule | null; token: ParseToken | null; cleanedText: string } => {
  // Try to match any recurrence pattern
  for (const pattern of RECURRENCE_PATTERNS) {
    const match = text.match(pattern);
    if (match && match.index !== undefined) {
      // Try to parse this match as a recurrence rule
      const recurrence = parseRecurrenceFromText(match[0]);
      if (recurrence) {
        const token: ParseToken = {
          type: 'recurrence',
          value: match[0],
          start: match.index,
          end: match.index + match[0].length,
          displayValue: formatRecurrence(recurrence),
        };

        // Remove the recurrence text from input
        const cleanedText = (text.slice(0, match.index) + text.slice(match.index + match[0].length))
          .replace(/\s+/g, ' ')
          .trim();

        return { recurrence, token, cleanedText };
      }
    }
  }

  return { recurrence: null, token: null, cleanedText: text };
};

/**
 * Parse date/time from input text using chrono-node
 */
const parseDateTime = (
  text: string,
  referenceDate?: Date
): {
  date: Date | null;
  hasTime: boolean;
  token: ParseToken | null;
  cleanedText: string;
} => {
  const ref = referenceDate || new Date();

  // Configure chrono to prefer future dates
  const results = chrono.parse(text, ref, { forwardDate: true });

  if (results.length === 0) {
    return { date: null, hasTime: false, token: null, cleanedText: text };
  }

  // Take the first (most relevant) result
  const result = results[0];
  const parsedDate = result.start.date();

  // Check if a specific time was mentioned
  const hasTime = result.start.isCertain('hour') || result.start.isCertain('minute');

  // Format display value
  let displayValue: string;
  if (hasTime) {
    displayValue = formatDateTimeDisplay(parsedDate);
  } else {
    displayValue = formatDateDisplay(parsedDate);
  }

  const token: ParseToken = {
    type: hasTime ? 'time' : 'date',
    value: result.text,
    start: result.index,
    end: result.index + result.text.length,
    displayValue,
  };

  // Remove the date text from input
  const cleanedText = (text.slice(0, result.index) + text.slice(result.index + result.text.length))
    .replace(/\s+/g, ' ')
    .trim();

  return { date: parsedDate, hasTime, token, cleanedText };
};

/**
 * Format date for display (without time)
 */
const formatDateDisplay = (date: Date): string => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Check if it's today
  if (isSameDay(date, now)) {
    return 'Today';
  }

  // Check if it's tomorrow
  if (isSameDay(date, tomorrow)) {
    return 'Tomorrow';
  }

  // Check if it's within this week (next 7 days)
  const daysUntil = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysUntil >= 0 && daysUntil < 7) {
    return date.toLocaleDateString('en-NZ', { weekday: 'long' });
  }

  // Otherwise show the date
  return date.toLocaleDateString('en-NZ', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Format date and time for display
 */
const formatDateTimeDisplay = (date: Date): string => {
  const dateStr = formatDateDisplay(date);
  const timeStr = date.toLocaleTimeString('en-NZ', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateStr} ${timeStr}`;
};

/**
 * Check if two dates are the same day
 */
const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Main function to parse a task input string
 */
export const parseTask = (input: string, referenceDate?: Date): ParsedTask => {
  if (!input.trim()) {
    return {
      title: '',
      dueDate: null,
      hasTime: false,
      priority: null,
      recurrence: null,
      tokens: [],
      originalText: input,
    };
  }

  const tokens: ParseToken[] = [];
  let workingText = input;

  // Parse priority first (simpler patterns)
  const priorityResult = parsePriority(workingText);
  if (priorityResult.token) {
    tokens.push(priorityResult.token);
    workingText = priorityResult.cleanedText;
  }

  // Parse recurrence before date/time (to avoid conflicts with chrono-node)
  const recurrenceResult = parseRecurrence(workingText);
  if (recurrenceResult.token) {
    tokens.push(recurrenceResult.token);
    workingText = recurrenceResult.cleanedText;
  }

  // Parse date/time
  const dateTimeResult = parseDateTime(workingText, referenceDate);
  if (dateTimeResult.token) {
    tokens.push(dateTimeResult.token);
    workingText = dateTimeResult.cleanedText;
  }

  // Sort tokens by their original position for consistent display
  tokens.sort((a, b) => a.start - b.start);

  return {
    title: workingText.trim(),
    dueDate: dateTimeResult.date,
    hasTime: dateTimeResult.hasTime,
    priority: priorityResult.priority,
    recurrence: recurrenceResult.recurrence,
    tokens,
    originalText: input,
  };
};

/**
 * Check if input contains any parseable tokens
 */
export const hasParseableTokens = (input: string): boolean => {
  const result = parseTask(input);
  return result.tokens.length > 0;
};

/**
 * Get a preview of what will be parsed without full parsing
 * Useful for real-time feedback with debouncing
 */
export const getParsePreview = (
  input: string,
  referenceDate?: Date
): {
  hasDate: boolean;
  hasTime: boolean;
  hasPriority: boolean;
  hasRecurrence: boolean;
} => {
  const result = parseTask(input, referenceDate);
  return {
    hasDate: result.dueDate !== null,
    hasTime: result.hasTime,
    hasPriority: result.priority !== null,
    hasRecurrence: result.recurrence !== null,
  };
};
